import { parseAndEvaluate } from "./safeExpressionParser";

export type DependsOnExpression = string | boolean | undefined;

/**
 * Context for how to interpret the result and handle errors.
 * - "visibility": true = visible (default for depends_on)
 * - "read_only": true = read-only (for read_only_depends_on)
 * - "mandatory": true = required (for mandatory_depends_on)
 */
export type EvalContext = "visibility" | "read_only" | "mandatory";

/**
 * Evaluate a depends_on expression safely using AST parser (no eval/Function).
 *
 * Supports:
 * - Simple fieldname: "fieldname" (truthy check)
 * - Eval expressions: "eval:doc.field == 'value'", "eval:doc.a && doc.b"
 * - Boolean values: true/false
 *
 * Unsupported eval patterns (cint(), in_list(), erpnext.*) fail gracefully
 * with a safe default based on context.
 */
export function evaluateDependsOn(
  expression: DependsOnExpression,
  doc: Record<string, any>,
  parent?: Record<string, any>,
  context: EvalContext = "visibility"
): boolean {
  if (expression === undefined || expression === null) return true;
  if (typeof expression === "boolean") return expression;

  if (typeof expression === "string") {
    // 1) eval: expression
    if (expression.startsWith("eval:")) {
      const expr = expression.slice(5).trim();

      try {
        return parseAndEvaluate(expr, { doc, parent });
      } catch {
        // Unsupported expression (cint, in_list, erpnext.* etc.)
        // Safe defaults: visible=true, read_only=false, required=false
        return context === "visibility";
      }
    }

    // 2) simple fieldname
    const value = doc[expression];
    return Array.isArray(value) ? value.length > 0 : !!value;
  }

  return true;
}
